// frontend/src/api/project.js
// Funciones API para la gestión de proyectos, fases y actividades.
// ACTUALIZADO: Añadida función para obtener los logs de auditoría de un proyecto.

import { request } from './api'; // Importa la función 'request' base

/**
 * @function createProject
 * @description Crea un nuevo proyecto asociado a un cliente.
 * @param {object} projectData - Datos del nuevo proyecto (incluye clientId).
 * @param {string} token - Token JWT para autorización.
 * @returns {Promise<object>} - Una promesa que resuelve con el proyecto creado.
 */
export async function createProject(projectData, token) {
  return request('/api/projects', 'POST', projectData, token);
}

/**
 * @function getAllProjects
 * @description Obtiene una lista de todos los proyectos, con filtros opcionales (ej: por clientId).
 * @param {string} token - Token JWT para autorización.
 * @param {object} [filters={}] - Objeto con filtros opcionales (ej: { clientId: '...' }).
 * @returns {Promise<Array<object>>} - Una promesa que resuelve con un array de objetos proyecto.
 */
export async function getAllProjects(token, filters = {}) {
  // Limpiar filtros nulos o vacíos para no ensuciar la URL
  const cleanFilters = Object.fromEntries(
    Object.entries(filters).filter(([_, v]) => v !== null && v !== '')
  );
  const queryParams = new URLSearchParams(cleanFilters).toString();
  const endpoint = `/api/projects${queryParams ? `?${queryParams}` : ''}`;
  return request(endpoint, 'GET', null, token);
}

/**
 * @function getProjectById
 * @description Obtiene los detalles de un proyecto específico por su ID.
 * Incluye sus fases y actividades.
 * @param {string} projectId - El ID único del proyecto.
 * @param {string} token - Token JWT para autorización.
 * @returns {Promise<object>} - Una promesa que resuelve con el objeto proyecto completo.
 */
export async function getProjectById(projectId, token) {
  return request(`/api/projects/${projectId}`, 'GET', null, token);
}

/**
 * @function updateProject
 * @description Actualiza la información de un proyecto existente.
 * @param {string} projectId - El ID único del proyecto a actualizar.
 * @param {object} projectData - Objeto con los datos actualizados del proyecto.
 * @param {string} token - Token JWT para autorización.
 * @returns {Promise<object>} - Una promesa que resuelve con el proyecto actualizado.
 */
export async function updateProject(projectId, projectData, token) {
  return request(`/api/projects/${projectId}`, 'PUT', projectData, token);
}

/**
 * @function deleteProject
 * @description Elimina un proyecto del backend.
 * @param {string} projectId - El ID único del proyecto a eliminar.
 * @param {string} token - Token JWT para autorización.
 * @returns {Promise<object>} - Una promesa que resuelve cuando el proyecto ha sido eliminado (respuesta vacía).
 */
export async function deleteProject(projectId, token) {
  return request(`/api/projects/${projectId}`, 'DELETE', null, token);
}

/**
 * @function syncProjectActivities
 * @description Solicita al backend que sincronice las actividades de un proyecto con las base.
 * @param {string} projectId - El ID del proyecto a sincronizar.
 * @param {string} token - Token JWT para autorización.
 * @returns {Promise<object>} - Una promesa que resuelve con el proyecto actualizado.
 */
export async function syncProjectActivities(projectId, token) {
  return request(`/api/projects/${projectId}/sync`, 'POST', null, token);
}

// --- Funciones para la gestión de Fases y Actividades (dentro de un proyecto) ---

/**
 * @function updatePhaseActivity
 * @description Actualiza el estado (completado) de una actividad específica dentro de una fase.
 * @param {string} activityId - El ID de la actividad a actualizar.
 * @param {boolean} isCompleted - El nuevo estado de completado de la actividad.
 * @param {string} token - Token JWT para autorización.
 * @returns {Promise<object>} - La actividad actualizada.
 */
export async function updatePhaseActivity(activityId, isCompleted, token) {
  // Asumimos un endpoint como PUT /api/activities/:activityId para actualizar una actividad
  // La lógica para actualizar el progreso de la fase se hará en el backend
  return request(`/api/activities/${activityId}`, 'PUT', { isCompleted }, token);
}

/**
 * @function updatePhaseProgress
 * @description Actualiza el progreso de una fase.
 * Nota: El progreso de la fase se calcula en el backend al actualizar actividades,
 * pero esta función podría usarse para actualizar 'isCompleted' de la fase o 'closedDate'.
 * @param {string} phaseId - El ID de la fase a actualizar.
 * @param {object} phaseData - Los datos a actualizar de la fase (ej: { isCompleted: true, closedDate: '...' }).
 * @param {string} token - Token JWT para autorización.
 * @returns {Promise<object>} - La fase actualizada.
 */
export async function updatePhaseProgress(phaseId, phaseData, token) {
  // Asumimos un endpoint como PUT /api/phases/:phaseId
  return request(`/api/phases/${phaseId}`, 'PUT', phaseData, token);
}

/**
 * @function getBaseActivities
 * @description Obtiene las actividades base/plantilla del backend.
 * @param {string} token - Token JWT para autorización.
 * @returns {Promise<object>} - Objeto con actividades base agrupadas por fase.
 */
export async function getBaseActivities(token) {
  return request('/api/base-activities', 'GET', null, token);
}

/**
 * @function addBaseActivity
 * @description Añade una nueva actividad base.
 * @param {object} activityData - { phaseName: string, description: string }
 * @param {string} token - Token JWT.
 * @returns {Promise<object>} - La actividad base creada.
 */
export async function addBaseActivity(activityData, token) {
  return request('/api/base-activities', 'POST', activityData, token);
}

/**
 * @function updateBaseActivity
 * @description Actualiza una actividad base existente.
 * @param {string} activityId - ID de la actividad base.
 * @param {object} activityData - Datos a actualizar.
 * @param {string} token - Token JWT.
 * @returns {Promise<object>} - La actividad base actualizada.
 */
export async function updateBaseActivity(activityId, activityData, token) {
  return request(`/api/base-activities/${activityId}`, 'PUT', activityData, token);
}

/**
 * @function deleteBaseActivity
 * @description Elimina una actividad base.
 * @param {string} activityId - ID de la actividad base.
 * @param {string} token - Token JWT.
 * @returns {Promise<object>} - Respuesta de éxito.
 */
export async function deleteBaseActivity(activityId, token) {
  return request(`/api/base-activities/${activityId}`, 'DELETE', null, token);
}

/**
 * @function getProjectAuditLogs
 * @description Obtiene los registros de auditoría para un proyecto específico.
 * @param {string} projectId - ID del proyecto.
 * @param {string} token - Token JWT para autorización.
 * @returns {Promise<Array<object>>} - Una promesa que resuelve con un array de objetos de log de auditoría.
 */
export async function getProjectAuditLogs(projectId, token) {
  return request(`/api/projects/${projectId}/audit-logs`, 'GET', null, token);
}
